// src/Components/Modules/HR/EventManagement.js
import React, { useState, useEffect } from 'react';
import { AxiosAuthInstance } from '../../../AxiosInterceptors';
import Breadcrumb from '../../Partials/Breadcrumb';
import {
    Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, FormControl, InputLabel, Select, MenuItem, Stack, LinearProgress,
    Collapse, IconButton
} from '@mui/material';
import { 
    FaPlus, FaCalendarAlt, FaMoneyBillWave, FaUsers, FaCheckCircle, FaEye, 
    FaChevronDown, FaChevronUp, FaFileInvoice, FaTrash 
} from 'react-icons/fa';
import Swal from 'sweetalert2';

export default function EventManagement() {
    const [events, setEvents] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [expenseAccounts, setExpenseAccounts] = useState([]);
    
    const [dialogOpen, setDialogOpen] = useState(false);
    const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
    const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
    
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedEventForBreakdown, setSelectedEventForBreakdown] = useState(null);
    const [expandedHeads, setExpandedHeads] = useState({});
    
    const [form, setForm] = useState({
        title: '', event_type: 'meeting', custom_event_type: '',
        start_date: '', end_date: '', location: '', description: '', attendees: []
    });
    
    // ✅ Dynamic Budget Allocation State
    const [budgetAllocations, setBudgetAllocations] = useState([{ account_id: '', amount: '' }]);
    
    const [expenseForm, setExpenseForm] = useState({ 
        account_id: '', description: '', amount: '', expense_date: new Date().toISOString().split('T')[0] 
    });

    const fetchData = async () => {
        try {
            const [eRes, empRes, accRes] = await Promise.all([
                AxiosAuthInstance.get('events'),
                AxiosAuthInstance.get('add_employee'),
                AxiosAuthInstance.get('chart-of-accounts?type=expense') 
            ]);
            setEvents(eRes.data.events || []);
            setEmployees(Array.isArray(empRes.data.employee) ? empRes.data.employee : []);
            const accountsData = accRes.data?.accounts || accRes.data;
            setExpenseAccounts(Array.isArray(accountsData) ? accountsData : []);
        } catch (err) {
            console.error("Failed to fetch data:", err);
            setExpenseAccounts([]);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // ✅ Budget Allocation Handlers
    const handleAddBudgetRow = () => setBudgetAllocations([...budgetAllocations, { account_id: '', amount: '' }]);
    const handleRemoveBudgetRow = (index) => {
        if (budgetAllocations.length > 1) {
            setBudgetAllocations(budgetAllocations.filter((_, i) => i !== index));
        }
    };
    const handleBudgetChange = (index, field, value) => {
        const newAllocations = [...budgetAllocations];
        newAllocations[index][field] = value;
        setBudgetAllocations(newAllocations);
    };
    const totalBudget = budgetAllocations.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    const handleSaveEvent = async () => {
        if (form.event_type === 'other' && !form.custom_event_type.trim()) {
            Swal.fire({ icon: "error", title: "Error", text: "Please specify the custom event type" });
            return;
        }
        if (totalBudget <= 0) {
            Swal.fire({ icon: "error", title: "Error", text: "Please add at least one valid budget allocation" });
            return;
        }

        try {
            const payload = {
                ...form,
                event_type: form.event_type === 'other' ? form.custom_event_type.trim() : form.event_type,
                budget_allocations: budgetAllocations // ✅ Backend এ পাঠানো হচ্ছে
            };

            await AxiosAuthInstance.post('events', payload);
            Swal.fire({ icon: 'success', title: 'Event Created!', timer: 1500, showConfirmButton: false });
            setDialogOpen(false);
            setForm({ title: '', event_type: 'meeting', custom_event_type: '', start_date: '', end_date: '', location: '', description: '', attendees: [] });
            setBudgetAllocations([{ account_id: '', amount: '' }]);
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
        }
    };

    const handleAddExpense = async () => {
        if (!expenseForm.account_id) {
            Swal.fire({ icon: "error", title: "Error", text: "Please select an Expense Head" });
            return;
        }
        try {
            await AxiosAuthInstance.post('event-expenses', { ...expenseForm, event_id: selectedEvent.id });
            Swal.fire({ icon: 'success', title: 'Expense Added & Accounting Updated!', timer: 1500, showConfirmButton: false });
            setExpenseDialogOpen(false);
            setExpenseForm({ account_id: '', description: '', amount: '', expense_date: new Date().toISOString().split('T')[0] });
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
        }
    };

    const getStatusColor = (status) => {
        const map = { planned: 'info', ongoing: 'warning', completed: 'success', cancelled: 'error' };
        return map[status] || 'default';
    };

    const toggleHeadExpand = (headName) => {
        setExpandedHeads(prev => ({ ...prev, [headName]: !prev[headName] }));
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            <Breadcrumb title="Event Management" />
            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight={700}>Company Events & Functions</Typography>
                <Button variant="contained" startIcon={<FaPlus />} onClick={() => setDialogOpen(true)}>Create Event</Button>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell>Event Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Budget vs Cost</TableCell>
                            <TableCell>Attendees</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {events.map((ev) => {
                            const costPercent = ev.budget > 0 ? Math.min((ev.actual_cost / ev.budget) * 100, 100) : 0;
                            return (
                                <TableRow key={ev.id} hover>
                                    <TableCell>
                                        <strong>{ev.title}</strong><br/>
                                        <Typography variant="caption" color="text.secondary">{ev.location}</Typography>
                                    </TableCell>
                                    <TableCell><Chip label={ev.event_type} size="small" sx={{ textTransform: 'capitalize' }} /></TableCell>
                                    <TableCell>{ev.start_date}</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>
                                        <Typography variant="body2">৳{Number(ev.actual_cost || 0).toLocaleString()} / ৳{Number(ev.budget || 0).toLocaleString()}</Typography>
                                        <LinearProgress variant="determinate" value={costPercent} sx={{ height: 6, borderRadius: 3, mt: 0.5, bgcolor: costPercent > 90 ? '#ffebee' : '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: costPercent > 90 ? '#f44336' : '#4caf50' } }} />
                                    </TableCell>
                                    <TableCell>{ev.attendees?.length || 0} Employees</TableCell>
                                    <TableCell><Chip label={ev.status} size="small" color={getStatusColor(ev.status)} /></TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            {/* ✅ BREAKDOWN BUTTON */}
                                            <Button size="small" variant="contained" color="info" startIcon={<FaEye />} onClick={() => { setSelectedEventForBreakdown(ev); setExpandedHeads({}); setBreakdownDialogOpen(true); }}>
                                                Breakdown
                                            </Button>
                                            <Button size="small" variant="outlined" startIcon={<FaMoneyBillWave />} onClick={() => { setSelectedEvent(ev); setExpenseDialogOpen(true); }}>
                                                Add Cost
                                            </Button>
                                            {ev.status === 'planned' && (
                                                <Button size="small" variant="contained" color="success" startIcon={<FaCheckCircle />} onClick={async () => { await AxiosAuthInstance.post(`events/${ev.id}/status/ongoing`); fetchData(); }}>
                                                    Start
                                                </Button>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create Event Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Create New Event</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Event Title *" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} fullWidth required />
                        
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Event Type</InputLabel>
                                <Select value={form.event_type} label="Event Type" onChange={(e) => setForm({...form, event_type: e.target.value, custom_event_type: ''})}>
                                    <MenuItem value="meeting">Meeting</MenuItem>
                                    <MenuItem value="picnic">Picnic</MenuItem>
                                    <MenuItem value="function">Function / Party</MenuItem>
                                    <MenuItem value="training">Training</MenuItem>
                                    <MenuItem value="team_building">Team Building</MenuItem>
                                    <MenuItem value="other">Other (Specify)</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {form.event_type === 'other' && (
                            <TextField label="Specify Event Type *" value={form.custom_event_type} onChange={(e) => setForm({...form, custom_event_type: e.target.value})} fullWidth required placeholder="e.g., Annual Dinner" sx={{ bgcolor: '#fff3e0', borderRadius: 1, p: 0.5 }} />
                        )}

                        {/* ✅ DYNAMIC BUDGET ALLOCATION */}
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Budget Allocation by Head</Typography>
                            {budgetAllocations.map((alloc, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                    <FormControl size="small" sx={{ flex: 2 }}>
                                        <InputLabel>Budget Head</InputLabel>
                                        <Select value={alloc.account_id} label="Budget Head" onChange={(e) => handleBudgetChange(index, 'account_id', e.target.value)}>
                                            {expenseAccounts.map(acc => <MenuItem key={acc.id} value={acc.id}>{acc.account_code} - {acc.account_name}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <TextField size="small" type="number" label="Amount (৳)" value={alloc.amount} onChange={(e) => handleBudgetChange(index, 'amount', e.target.value)} sx={{ flex: 1 }} />
                                    <IconButton size="small" color="error" onClick={() => handleRemoveBudgetRow(index)} disabled={budgetAllocations.length === 1}>
                                        <FaTrash />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button size="small" startIcon={<FaPlus />} onClick={handleAddBudgetRow} sx={{ mb: 2 }}>Add Budget Head</Button>
                            <Typography variant="h6" color="primary" sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, textAlign: 'center' }}>
                                Total Budget: ৳{totalBudget.toLocaleString()}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Start Date *" type="date" size="small" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} InputLabelProps={{shrink: true}} fullWidth required />
                            <TextField label="End Date" type="date" size="small" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} InputLabelProps={{shrink: true}} fullWidth />
                        </Box>
                        <TextField label="Location" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} fullWidth />
                        <TextField label="Description" multiline rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} fullWidth />
                        
                        <FormControl fullWidth size="small">
                            <InputLabel>Invite Employees (Optional)</InputLabel>
                            <Select multiple value={form.attendees} label="Invite Employees" onChange={(e) => setForm({...form, attendees: e.target.value})}>
                                {employees.map(emp => <MenuItem key={emp.id} value={emp.id}>{emp.employee_name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEvent}>Create Event</Button>
                </DialogActions>
            </Dialog>

            {/* Add Expense Dialog */}
            <Dialog open={expenseDialogOpen} onClose={() => setExpenseDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Expense for: {selectedEvent?.title}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Expense Head (Account)</InputLabel>
                            <Select value={expenseForm.account_id} label="Expense Head (Account)" onChange={(e) => setExpenseForm({...expenseForm, account_id: e.target.value})}>
                                {expenseAccounts.map(acc => <MenuItem key={acc.id} value={acc.id}>{acc.account_code} - {acc.account_name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Expense Description *" value={expenseForm.description} onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})} fullWidth required />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Amount (৳) *" type="number" value={expenseForm.amount} onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})} fullWidth required />
                            <TextField label="Date *" type="date" size="small" value={expenseForm.expense_date} onChange={(e) => setExpenseForm({...expenseForm, expense_date: e.target.value})} InputLabelProps={{shrink: true}} fullWidth required />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExpenseDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddExpense}>Add Expense</Button>
                </DialogActions>
            </Dialog>

            {/* ✅ UPGRADED BREAKDOWN DIALOG (Summary + Accordion + Zero State) */}
            <Dialog open={breakdownDialogOpen} onClose={() => setBreakdownDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: "#f5f7fa", fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaFileInvoice /> Budget vs Actual Breakdown: {selectedEventForBreakdown?.title}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {/* 1. Total Budget, Total Spent, Remaining */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">Total Budget</Typography>
                            <Typography variant="h5" fontWeight={700} color="primary.main">৳{Number(selectedEventForBreakdown?.budget || 0).toLocaleString()}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Total Spent</Typography>
                            <Typography variant="h5" fontWeight={700} color="error.main">৳{Number(selectedEventForBreakdown?.actual_cost || 0).toLocaleString()}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="text.secondary">Remaining Budget</Typography>
                            <Typography variant="h5" fontWeight={700} color="success.main">
                                ৳{Number((selectedEventForBreakdown?.budget || 0) - (selectedEventForBreakdown?.actual_cost || 0)).toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaMoneyBillWave /> Actual Expense Breakdown by Head
                    </Typography>

                    {/* 2. Accordion & 3. Zero Data Handling */}
                    {(() => {
                        const expensesByHead = {};
                        (selectedEventForBreakdown?.expenses || []).forEach(exp => {
                            const headName = exp.account?.account_name || 'Unassigned Head';
                            if (!expensesByHead[headName]) {
                                expensesByHead[headName] = { total: 0, details: [] };
                            }
                            expensesByHead[headName].total += parseFloat(exp.amount || 0);
                            expensesByHead[headName].details.push(exp);
                        });

                        const headNames = Object.keys(expensesByHead);

                        if (headNames.length === 0) {
                            // ✅ ZERO DATA HANDLING
                            return (
                                <Box sx={{ textAlign: "center", py: 6, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed #ccc' }}>
                                    <FaMoneyBillWave size={40} style={{ color: "#bdbdbd", marginBottom: 12 }} />
                                    <Typography variant="h6" color="text.secondary">No expenses recorded yet.</Typography>
                                    <Typography variant="body2" color="text.secondary">Click "Add Cost" to start tracking expenses for this event.</Typography>
                                </Box>
                            );
                        }

                        return headNames.map((headName, idx) => {
                            const isExpanded = expandedHeads[headName];
                            const headData = expensesByHead[headName];
                            
                            return (
                                <Paper key={idx} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                                    <Box 
                                        sx={{ 
                                            p: 2, 
                                            bgcolor: isExpanded ? '#f0f4f8' : '#ffffff', 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s'
                                        }}
                                        onClick={() => toggleHeadExpand(headName)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Chip label={headName} color="primary" variant="outlined" size="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                {headData.details.length} transaction(s)
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Typography variant="h6" fontWeight={700} color="error.main">
                                                ৳{Number(headData.total).toLocaleString()}
                                            </Typography>
                                            <IconButton size="small">
                                                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    
                                    {/* ✅ ACCORDION EXPAND/COLLAPSE */}
                                    <Collapse in={isExpanded}>
                                        <TableContainer sx={{ maxHeight: 250, overflowY: 'auto' }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                                        <TableCell>Description</TableCell>
                                                        <TableCell align="right">Amount</TableCell>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell align="center">Status</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {headData.details.map((exp, expIdx) => (
                                                        <TableRow key={expIdx} hover>
                                                            <TableCell>{exp.description}</TableCell>
                                                            <TableCell align="right">৳{Number(exp.amount).toLocaleString()}</TableCell>
                                                            <TableCell>{exp.expense_date}</TableCell>
                                                            <TableCell align="center">
                                                                <Chip label={exp.status} size="small" color={exp.status === 'approved' ? 'success' : 'warning'} />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Collapse>
                                </Paper>
                            );
                        });
                    })()}
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: "#f5f7fa" }}>
                    <Button onClick={() => setBreakdownDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}